/**
 * Dateivorschau (0.91): welche Darstellung eine Datei bekommt. Gemeinsam für Server (Umwandlung, Auslieferung)
 * und Browser (AttachmentPreview, Symbole). Der Typ kommt vom Browser beim Hochladen und ist oft ungenau
 * (leer, `application/octet-stream`, `application/zip` für .docx) – darum zählt die Dateiendung mit.
 */

export type FilePreviewKind = 'image' | 'pdf' | 'office' | 'video' | 'audio' | 'text' | 'none';

const ext = (filename: string) => {
  const m = /\.([a-z0-9]{1,8})$/i.exec(filename.trim());
  return m ? m[1]!.toLowerCase() : '';
};

/** Office- und OpenDocument-Formate, die der Server mit LibreOffice in ein PDF umwandelt */
export const OFFICE_EXTENSIONS: Record<string, string> = {
  doc: 'application/msword',
  dot: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  docm: 'application/vnd.ms-word.document.macroEnabled.12',
  dotx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
  rtf: 'application/rtf',
  odt: 'application/vnd.oasis.opendocument.text',
  ott: 'application/vnd.oasis.opendocument.text-template',
  pages: 'application/vnd.apple.pages',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xlsm: 'application/vnd.ms-excel.sheet.macroEnabled.12',
  xltx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
  ods: 'application/vnd.oasis.opendocument.spreadsheet',
  ots: 'application/vnd.oasis.opendocument.spreadsheet-template',
  numbers: 'application/vnd.apple.numbers',
  ppt: 'application/vnd.ms-powerpoint',
  pps: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  pptm: 'application/vnd.ms-powerpoint.presentation.macroEnabled.12',
  ppsx: 'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
  potx: 'application/vnd.openxmlformats-officedocument.presentationml.template',
  odp: 'application/vnd.oasis.opendocument.presentation',
  otp: 'application/vnd.oasis.opendocument.presentation-template',
  key: 'application/vnd.apple.keynote',
  odg: 'application/vnd.oasis.opendocument.graphics',
  vsdx: 'application/vnd.ms-visio.drawing',
};

const OFFICE_MIMES = new Set(Object.values(OFFICE_EXTENSIONS).filter((m) => !m.startsWith('application/vnd.apple.')));

/** Medien: was Browser üblicherweise abspielen. Was nicht klappt, meldet das Element selbst (Hinweis + Download). */
export const MEDIA_EXTENSIONS: Record<string, string> = {
  mp4: 'video/mp4',
  m4v: 'video/x-m4v',
  mov: 'video/quicktime',
  webm: 'video/webm',
  ogv: 'video/ogg',
  mkv: 'video/x-matroska',
  avi: 'video/x-msvideo',
  '3gp': 'video/3gpp',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  oga: 'audio/ogg',
  opus: 'audio/ogg',
  flac: 'audio/flac',
  weba: 'audio/webm',
  aif: 'audio/aiff',
  aiff: 'audio/aiff',
};

/** Bilder, die Browser oft ohne Typ schicken (iPhone-Fotos aus „Dateien“ oder vom Mac) */
const IMAGE_EXTENSIONS: Record<string, string> = { heic: 'image/heic', heif: 'image/heif', hif: 'image/heif' };

/** Textartige Dateien, die nur als Text (nie als HTML) gezeigt werden */
const TEXT_EXTENSIONS = new Set([
  'txt', 'md', 'markdown', 'csv', 'tsv', 'log', 'json', 'xml', 'yml', 'yaml', 'toml', 'ini', 'conf', 'cfg', 'env',
  'js', 'mjs', 'cjs', 'ts', 'tsx', 'jsx', 'css', 'scss', 'less', 'py', 'rb', 'php', 'java', 'kt', 'swift', 'go', 'rs',
  'c', 'h', 'cpp', 'hpp', 'cs', 'sh', 'bash', 'zsh', 'ps1', 'bat', 'sql', 'vcf', 'ics', 'srt', 'vtt', 'tex', 'diff', 'patch',
]);
const TEXT_MIMES = new Set(['application/json', 'application/xml', 'application/x-yaml', 'application/yaml', 'application/javascript', 'application/x-sh', 'application/sql', 'application/x-tex']);

const GENERIC = new Set(['', 'application/octet-stream', 'application/zip', 'application/x-zip-compressed', 'binary/octet-stream']);

/**
 * Typ für eine hochgeladene Datei: den Angaben des Browsers folgen, außer sie sind leer oder allgemein – dann
 * aus der Endung. Damit spielen Videos auch in Safari (der verlässt sich auf Content-Type).
 */
export function uploadMime(type: string | undefined | null, filename: string): string {
  const t = (type ?? '').toLowerCase();
  const e = ext(filename);
  const known = OFFICE_EXTENSIONS[e] ?? MEDIA_EXTENSIONS[e] ?? IMAGE_EXTENSIONS[e];
  if (GENERIC.has(t) && known) return known;
  if (!t && TEXT_EXTENSIONS.has(e)) return 'text/plain';
  return type || 'application/octet-stream';
}

export function isOfficeFile(f: { mime: string; filename: string }): boolean {
  const e = ext(f.filename);
  // Apple-Formate nur über die Endung (der Typ wird selten gesetzt)
  return e in OFFICE_EXTENSIONS || OFFICE_MIMES.has(f.mime);
}

/**
 * Art der Vorschau. Bilder gelten erst als Bild, wenn der Server sie verarbeiten konnte (`isImage`).
 * HTML und SVG werden nie eingebettet.
 */
export function filePreviewKind(f: { mime: string; filename: string; isImage: boolean }): FilePreviewKind {
  if (f.isImage) return 'image';
  const mime = f.mime.toLowerCase();
  const e = ext(f.filename);
  if (mime === 'application/pdf' || (GENERIC.has(mime) && e === 'pdf')) return 'pdf';
  if (isOfficeFile({ mime, filename: f.filename })) return 'office';
  if (mime.startsWith('video/') || (GENERIC.has(mime) && MEDIA_EXTENSIONS[e]?.startsWith('video/'))) return 'video';
  if (mime.startsWith('audio/') || (GENERIC.has(mime) && MEDIA_EXTENSIONS[e]?.startsWith('audio/'))) return 'audio';
  if (mime === 'text/html' || mime === 'image/svg+xml' || e === 'html' || e === 'htm' || e === 'svg') return 'none';
  if (mime.startsWith('text/') || TEXT_MIMES.has(mime) || mime.endsWith('+json') || ((GENERIC.has(mime) || mime === 'text/plain') && TEXT_EXTENSIONS.has(e))) return 'text';
  return 'none';
}

/** Markdown erkennen (Vorschau zeigt es formatiert, aber ohne HTML) */
export function isMarkdownFile(f: { mime: string; filename: string }): boolean {
  const e = ext(f.filename);
  return f.mime === 'text/markdown' || f.mime === 'text/x-markdown' || e === 'md' || e === 'markdown';
}

/** CSV/TSV als Tabelle */
export function delimitedKind(f: { mime: string; filename: string }): ',' | ';' | '\t' | null {
  const e = ext(f.filename);
  if (e === 'tsv' || f.mime === 'text/tab-separated-values') return '\t';
  if (e === 'csv' || f.mime === 'text/csv') return ',';
  return null;
}

/** Grobe Gruppe für Symbole */
export function fileIconKind(f: { mime: string; filename: string; isImage: boolean }): 'image' | 'pdf' | 'word' | 'sheet' | 'slides' | 'video' | 'audio' | 'text' | 'archive' | 'file' {
  const k = filePreviewKind(f);
  if (k === 'image' || k === 'pdf' || k === 'video' || k === 'audio' || k === 'text') return k;
  const e = ext(f.filename);
  if (k === 'office') {
    if (/^(xls|xlsx|xlsm|xltx|ods|ots|numbers)$/.test(e) || /spreadsheet|excel/.test(f.mime)) return 'sheet';
    if (/^(ppt|pps|pptx|pptm|ppsx|potx|odp|otp|key)$/.test(e) || /presentation|powerpoint/.test(f.mime)) return 'slides';
    return 'word';
  }
  if (/^(zip|7z|rar|tar|gz|tgz|bz2|xz)$/.test(e)) return 'archive';
  return 'file';
}
